// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import React, { useCallback, useEffect, useRef, useState } from 'react';

// VAPT WEB-03: signs the recruiter out after a period of inactivity. The server
// enforces the same limit (SESSION_IDLE_TIMEOUT); this guard warns the user first
// and keeps the server session alive while they are active between API calls.

const DEFAULT_IDLE_TIMEOUT_MS = 30 * 60 * 1000;
const WARNING_BEFORE_MS = 2 * 60 * 1000;
const KEEPALIVE_INTERVAL_MS = 5 * 60 * 1000;
const CHECK_INTERVAL_MS = 15 * 1000;
// Shared across tabs so activity in one tab keeps the others signed in.
const ACTIVITY_STORAGE_KEY = 'g42.recruiter.lastActivity';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'] as const;

function readSharedActivity(): number {
  try {
    return Number(window.localStorage.getItem(ACTIVITY_STORAGE_KEY)) || 0;
  } catch {
    return 0;
  }
}

function writeSharedActivity(at: number): void {
  try {
    window.localStorage.setItem(ACTIVITY_STORAGE_KEY, String(at));
  } catch {
    // Storage unavailable (private mode); this tab still tracks its own activity.
  }
}

function signOut(): void {
  window.location.assign('/api/sso/logout');
}

interface IdleSessionGuardProps {
  idleTimeoutMs?: number;
}

const IdleSessionGuard: React.FC<IdleSessionGuardProps> = ({ idleTimeoutMs = DEFAULT_IDLE_TIMEOUT_MS }) => {
  const lastActivityRef = useRef(Date.now());
  const lastKeepaliveRef = useRef(Date.now());
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const sendKeepalive = useCallback(async (): Promise<void> => {
    lastKeepaliveRef.current = Date.now();
    try {
      const response = await fetch('/api/sso/keepalive', { method: 'POST', credentials: 'include' });
      if (response.status === 401) {
        const returnPath = `${window.location.pathname}${window.location.search}`;
        window.location.assign(`/login?returnTo=${encodeURIComponent(returnPath)}`);
      }
    } catch (error) {
      console.error('[IdleSessionGuard] Keepalive failed:', error);
    }
  }, []);

  const recordActivity = useCallback((): void => {
    const now = Date.now();
    if (now - lastActivityRef.current < 1000) return; // throttle high-frequency events
    lastActivityRef.current = now;
    writeSharedActivity(now);
    if (now - lastKeepaliveRef.current >= KEEPALIVE_INTERVAL_MS) {
      void sendKeepalive();
    }
  }, [sendKeepalive]);

  useEffect(() => {
    writeSharedActivity(lastActivityRef.current);
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, recordActivity, { passive: true }));

    const timer = window.setInterval(() => {
      const lastActivity = Math.max(lastActivityRef.current, readSharedActivity());
      const remaining = idleTimeoutMs - (Date.now() - lastActivity);
      if (remaining <= 0) {
        signOut();
        return;
      }
      setSecondsLeft(remaining <= WARNING_BEFORE_MS ? Math.ceil(remaining / 1000) : null);
    }, CHECK_INTERVAL_MS);

    return () => {
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, recordActivity));
      window.clearInterval(timer);
    };
  }, [idleTimeoutMs, recordActivity]);

  const staySignedIn = (): void => {
    const now = Date.now();
    lastActivityRef.current = now;
    writeSharedActivity(now);
    setSecondsLeft(null);
    void sendKeepalive();
  };

  if (secondsLeft === null) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="idle-session-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 10000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(0, 0, 0, 0.45)'
      }}
    >
      <div style={{ background: '#fff', borderRadius: 8, padding: 24, maxWidth: 380, width: 'calc(100% - 32px)' }}>
        <h2 id="idle-session-title" style={{ margin: '0 0 8px', fontSize: 18 }}>
          Are you still there?
        </h2>
        <p style={{ margin: '0 0 20px', fontSize: 14 }}>
          You will be signed out in about {Math.max(1, Math.ceil(secondsLeft / 60))} minute
          {secondsLeft > 60 ? 's' : ''} due to inactivity.
        </p>
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <button type="button" onClick={signOut}>
            Sign out
          </button>
          <button type="button" onClick={staySignedIn} autoFocus>
            Stay signed in
          </button>
        </div>
      </div>
    </div>
  );
};

export default IdleSessionGuard;
